// src/components/email/EmailDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

function EmailDashboard() {
  const [stats, setStats] = useState({
    totalCampanas: 0,
    totalEnviados: 0,
    totalFallidos: 0,
    tasaEntrega: 0
  });
  const [campanasRecientes, setCampanasRecientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [campanasRes] = await Promise.all([
        api.get('/email/campanas')
      ]);

      const campanas = campanasRes.data.campanas || [];
      const totalEnviados = campanas.reduce((sum, c) => sum + (c.total_enviados || 0), 0);
      const totalFallidos = campanas.reduce((sum, c) => sum + (c.total_fallidos || 0), 0);
      const tasaEntrega = totalEnviados + totalFallidos > 0 
        ? ((totalEnviados / (totalEnviados + totalFallidos)) * 100).toFixed(2) 
        : 0;

      setStats({
        totalCampanas: campanas.length,
        totalEnviados,
        totalFallidos,
        tasaEntrega
      });

      setCampanasRecientes(campanas.slice(0, 5));
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Cargando...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📧 Email Marketing</h1>
        <div style={styles.headerActions}>
          <Link to="/email/campanas/nueva" style={styles.btnPrimary}>
            <span>➕</span> Nueva Campaña
          </Link>
          <Link to="/email/plantillas" style={styles.btnSecondary}>
            <span>📄</span> Plantillas
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Campañas</div>
          <div style={styles.statValue}>{stats.totalCampanas}</div>
        </div>
        <div style={{ ...styles.statCard, ...styles.statCardSuccess }}>
          <div style={styles.statLabel}>Emails Enviados</div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.totalEnviados}</div>
        </div>
        <div style={{ ...styles.statCard, ...styles.statCardDanger }}>
          <div style={styles.statLabel}>Emails Fallidos</div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.totalFallidos}</div>
        </div>
        <div style={{ ...styles.statCard, ...styles.statCardPurple }}>
          <div style={styles.statLabel}>Tasa de Entrega</div>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{stats.tasaEntrega}%</div>
        </div>
      </div>

      {/* Campañas Recientes */}
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <h2 style={styles.sectionTitle}>Campañas Recientes</h2>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nombre</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}>Enviados</th>
              <th style={styles.th}>Fallidos</th>
              <th style={styles.th}>Fecha</th>
              <th style={styles.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {campanasRecientes.length > 0 ? (
              campanasRecientes.map(campana => (
                <tr key={campana.id} style={styles.tr}>
                  <td style={styles.td}>{campana.nombre}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(campana.estado === 'completado' ? styles.badgeSuccess :
                        campana.estado === 'enviando' ? styles.badgeInfo :
                        styles.badgeDanger)
                    }}>
                      {campana.estado}
                    </span>
                  </td>
                  <td style={styles.td}>{campana.total_enviados || 0}</td>
                  <td style={styles.td}>{campana.total_fallidos || 0}</td>
                  <td style={styles.td}>{new Date(campana.created_at).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <Link to={`/email/campanas/${campana.id}`} style={styles.link}>Ver detalle</Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center' }}>
                  No hay campañas registradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  headerActions: {
    display: 'flex',
    gap: '12px'
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s'
  },
  btnSecondary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    color: 'white',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
    transition: 'all 0.2s'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderLeft: '4px solid #3b82f6'
  },
  statCardSuccess: {
    borderLeftColor: '#10b981'
  },
  statCardDanger: {
    borderLeftColor: '#ef4444'
  },
  statCardPurple: {
    borderLeftColor: '#8b5cf6'
  },
  statLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '600',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    lineHeight: '1'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden'
  },
  tableHeader: {
    padding: '20px',
    borderBottom: '2px solid #e5e7eb'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151'
  },
  tr: {
    transition: 'background 0.2s'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '20px',
    textTransform: 'uppercase'
  },
  badgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  badgeDanger: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  badgeInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontWeight: '600'
  }
};

export default EmailDashboard;